import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IContactStatusDeprecatedService } from '@agent/core/contact/contact-status-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { ILogService, IPromise, IQService, isDefined } from 'angular';
import { Observable, Subject } from 'rxjs';

import {
  BookingStatus,
  COMETD_GUEST_CREATION,
  COMETD_GUEST_STATE,
  CSR_FACADE_PREFIX,
  IStateEvent,
  MessagingService,
  None,
  Option,
  StorageService,
} from '@contact/upgrade';

import { Guest, GuestState, GuestType, IConference, IGuest } from './models/guest';

const MODULE_NAME = 'conferenceDeprecatedService';
const CONFERENCES_KEY = 'conferences';

export interface IConferenceDeprecatedService {
  parkContact(responseId: string): IPromise<any>;

  unparkContact(responseId: string): IPromise<any>;

  isContactParked(responseId: string): boolean;

  isAgentParked(responseId: string): boolean;

  getTargetGuests(responseId: string): IGuest[];

  getContactGuest(responseId: string): Option<IGuest>;

  getAgentGuest(responseId: string): Option<IGuest>;

  getSupervisorGuests(responseId: string): IGuest[];

  removeGuest(guestId: string): void;

  parkGuest(guestId: string): IPromise<any>;

  unparkGuest(guestId: string): IPromise<any>;

  parkAgent(responseId: string): IPromise<any>;

  unparkAgent(responseId: string): IPromise<any>;

  validateTransfer(responseId: string, guestId: string): IPromise<any>;

  cancelTransfer(responseId: string): IPromise<any>;

  isConferencing(responseId: string): boolean;

  getGuests(responseId: string): IGuest[];

  unparkContactAndAgent(responseId: string): IPromise<any>;

  setContactParked(responseId: string): void;

  removeConference(conferenceId: string): void;

  guestState$(): Observable<IGuest>;
}

export class ConferenceDeprecatedService implements IConferenceDeprecatedService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    'httpDeprecated',
    'messaging',
    'contactStatusDeprecatedService',
    'storage',
    'contactDeprecatedService',
  ];

  public name = MODULE_NAME;

  private conferences: { [id: string]: IGuest[] } = {};
  private _guestState$: Subject<IGuest> = new Subject<IGuest>();

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly messaging: MessagingService,
    private readonly contactStatusDeprecatedService: IContactStatusDeprecatedService,
    private readonly storage: StorageService,
    private readonly contactDeprecatedService: IContactDeprecatedService
  ) {
    this.conferences = this.storage.get(CONFERENCES_KEY).getOrElse({});

    // contact state success
    this.contactStatusDeprecatedService.success().subscribe(data => this.contactStateListener(data));

    // GUEST_CREATION_CHANNEL
    this.messaging
      .forRemote()
      .listen$(COMETD_GUEST_CREATION)
      .subscribe(dto => this.guestCreationListener(dto));

    // GUEST_STATE_CHANNEL
    this.messaging
      .forRemote()
      .listen$(COMETD_GUEST_STATE)
      .subscribe(dto => this.guestStateListener(dto));
  }

  public removeConference(id: string): void {
    this.getConference(id).forEach(conference => {
      conference.forEach(guest => {
        this.updateGuestState(guest.id, GuestState.INITIALIZED);
      });
    });

    delete this.conferences[id];
    this.saveConferences();
    this.$log.info(`[conference] removed conference with id[${id}]`);
  }

  parkContact(responseId: string): IPromise<any> {
    return this.$q.all([
      this.httpDeprecated.asPromise().post(CSR_FACADE_PREFIX + '/parkContact', [responseId]),
      this.createContactGuest(responseId).then(guest => {
        this.addGuest(guest);
        this.updateGuestState(guest.id, GuestState.PARKED);
      }),
    ]);
  }

  parkAgent(responseId: string): IPromise<any> {
    return this.createAgentGuest(responseId).then(guest => {
      this.addGuest(guest);
      this.updateGuestState(guest.id, GuestState.PARKED);
    });
  }

  unparkContact(responseId: string): IPromise<any> {
    return this.$q.all([
      this.httpDeprecated.asPromise().post(CSR_FACADE_PREFIX + '/unparkContact', [responseId]),
      this.getContact(responseId).then(guest => {
        this.updateGuestState(guest.id, GuestState.CONNECTED);
      }),
    ]);
  }

  unparkAgent(responseId: string): IPromise<any> {
    return this.$q.all([
      this.httpDeprecated.asPromise().post(CSR_FACADE_PREFIX + '/unparkCsr', [responseId]),
      this.createAgentGuest(responseId).then(guest => {
        this.updateGuestState(guest.id, GuestState.CONNECTED);
      }),
    ]);
  }

  unparkContactAndAgent(responseId: string): IPromise<any> {
    return this.$q.all([
      this.httpDeprecated.asPromise().post(CSR_FACADE_PREFIX + '/unparkContactAndCsr', [responseId]),
      this.getContact(responseId).then(
        guest => {
          this.updateGuestState(guest.id, GuestState.CONNECTED);
        },
        () => {
          this.createContactGuest(responseId).then(guest => {
            this.updateGuestState(guest.id, GuestState.CONNECTED);
          });
        }
      ),
      this.getAgentGuest(responseId).forEach(csr => {
        this.updateGuestState(csr.id, GuestState.CONNECTED);
      }),
    ]);
  }

  parkGuest(guestId: string): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .post(CSR_FACADE_PREFIX + '/parkParticipant', [guestId])
      .then(() => {
        this.updateGuestState(guestId, GuestState.PARKED);
      });
  }

  unparkGuest(guestId: string): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .post(CSR_FACADE_PREFIX + '/unparkParticipant', [guestId])
      .then(() => {
        this.updateGuestState(guestId, GuestState.CONNECTED);
      });
  }

  validateTransfer(responseId: string, guestId: string): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .post(CSR_FACADE_PREFIX + '/transferParticipantTo', [responseId, guestId])
      .then(() => {
        this.removeConference(responseId);
      });
  }

  cancelTransfer(responseId: string): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .post(CSR_FACADE_PREFIX + '/cancelConference', [responseId])
      .then(() => {
        this.removeConference(responseId);
        this.unparkContactAndAgent(responseId);
      });
  }

  isContactParked(responseId: string): boolean {
    let contactGuests = this.getGuestsByType(responseId, GuestType.CONTACT);
    if (contactGuests.length > 0) {
      return contactGuests[0].state === GuestState.PARKED;
    }
    return false;
  }

  isAgentParked(responseId: string): boolean {
    let targetGuests = this.getGuestsByType(responseId, GuestType.TARGET);
    if (targetGuests.length > 0) {
      return targetGuests[0].state === GuestState.PARKED;
    }
    return false;
  }

  setContactParked(responseId: string): void {
    let contactGuests = this.getGuestsByType(responseId, GuestType.CONTACT);
    if (contactGuests.length > 0) {
      contactGuests[0].state = GuestState.PARKED;
    } else {
      this.createContactGuest(responseId).then(guest => {
        this.addGuest(guest);
        this.updateGuestState(guest.id, GuestState.PARKED);
      });
    }
  }

  getTargetGuests(responseId: string): IGuest[] {
    return this.findGuestsByType(responseId, GuestType.TARGET);
  }

  getContactGuest(responseId: string): Option<IGuest> {
    let guests = this.findGuestsByType(responseId, GuestType.CONTACT);
    return Option.of(guests[0]);
  }

  getAgentGuest(responseId: string): Option<IGuest> {
    let guests = this.findGuestsByType(responseId, GuestType.AGENT);
    return Option.of(guests[0]);
  }

  getSupervisorGuests(responseId: string): IGuest[] {
    return this.findGuestsByType(responseId, GuestType.SUPERVISOR);
  }

  removeGuest(guestId: string): void {
    this.findGuest(guestId).forEach(guest => {
      this._removeGuest(guest);
    });
  }

  isConferencing(responseId: string): boolean {
    return this.getConference(responseId)
      .map(guests => guests.length > 2 && guests.every(guest => guest.state === GuestState.CONNECTED))
      .getOrElse(false);
  }

  getGuests(responseId: string): IGuest[] {
    return this.getConference(responseId).getOrElse([]);
  }

  guestState$(): Observable<IGuest> {
    return this._guestState$.asObservable();
  }

  private contactStateListener(event: IStateEvent<BookingStatus>) {
    if (event.newState === BookingStatus.INTER_CONTACT || event.newState === BookingStatus.CLOSED) {
      if (event && event.id) {
        this.removeConference(event.id);
      }
    }
  }

  private guestCreationListener(dto) {
    Guest.fromDto(dto).forEach(guest => {
      this.addGuest(guest);
    });
  }

  private guestStateListener(dto) {
    let guestId = dto.participantUuid;
    let guestState = Guest.stateFromDto(dto.status as string);
    this.updateGuestState(guestId, guestState);
  }

  private saveConferences(): void {
    this.storage.store(CONFERENCES_KEY, this.conferences);
  }

  private createConference(id: string): IConference {
    if (isDefined(this.conferences[id])) {
      return this.conferences[id];
    }
    this.conferences[id] = [];
    this.saveConferences();
    this.$log.info(`[conference] created conference with id[${id}]`);
    return this.conferences[id];
  }

  private getConference(id: string): Option<IConference> {
    return Option.of(this.conferences[id]);
  }

  private addGuest(guest: IGuest): void {
    let conferenceId = guest.responseId;
    let conference = this.getConference(conferenceId).getOrElse(this.createConference(conferenceId));
    let index = conference.map(confGuest => confGuest.id).indexOf(guest.id);
    if (index < 0) {
      conference.push(guest);
      this.$log.info(
        `[conference] added guest[${GuestType[guest.type]}] with id[${guest.id}] and state[${
          GuestState[guest.state]
        }] to conference with id[${conferenceId}].`
      );
    } else {
      let oldGuest = conference[index];
      conference.splice(index, 1, guest);
      this.$log.info(
        `[conference] replaced guest[${GuestType[guest.type]}] with id[${guest.id}] and state[${
          GuestState[oldGuest.state]
        }] by same guest with state[${GuestState[guest.state]}] in conference with id[${conferenceId}].`
      );
    }
    this.saveConferences();
  }

  private _removeGuest(guest: IGuest): void {
    let conferenceId = guest.responseId;
    this.getConference(conferenceId).forEach(conference => {
      let index = conference.map(confGuest => confGuest.id).indexOf(guest.id);
      if (index >= 0) {
        conference.splice(index, 1);
        guest.removed = true;
        this._guestState$.next(guest);
        this.$log.info(
          `[conference] removed guest[${GuestType[guest.type]}] with id[${guest.id}] from conference with id[${conferenceId}].`
        );
        if (conference.length === 0) {
          this.removeConference(conferenceId);
        } else {
          this.saveConferences();
        }
      }
    });
  }

  private getGuestsByType(responseId: string, type: GuestType): IGuest[] {
    return this.getConference(responseId)
      .map(conference => conference.filter(guest => guest.type === type))
      .getOrElse([]);
  }

  private getGuest(responseId: string, guestId: string): Option<IGuest> {
    return this.getConference(responseId).flatMap(conference => {
      let index = conference.map(guest => guest.id).indexOf(guestId);
      if (index < 0) {
        return None;
      }
      return Option.of(conference[index]);
    });
  }

  private findGuest(guestId: string): Option<IGuest> {
    for (let responseId in this.conferences) {
      let guestOpt = this.getGuest(responseId, guestId);
      if (guestOpt.isDefined()) {
        return guestOpt;
      }
    }
    return None;
  }

  private findGuestsByType(responseId: string, type: GuestType): IGuest[] {
    return this.getConference(responseId)
      .map(conference => conference.filter(guest => guest.type === type))
      .getOrElse([]);
  }

  private createContactGuest(responseId: string, state: GuestState = GuestState.INITIALIZED): IPromise<IGuest> {
    return this.contactDeprecatedService.getContact(responseId).then(contact => ({
      type: GuestType.CONTACT,
      responseId: responseId,
      id: contact.connectionId,
      conferenceId: responseId,
      state: state,
      removed: false,
    }));
  }

  private createAgentGuest(responseId: string, state: GuestState = GuestState.INITIALIZED): IPromise<IGuest> {
    return this.contactDeprecatedService.getContact(responseId).then(contact => ({
      type: GuestType.AGENT,
      responseId: responseId,
      id: contact.agentConnectionId,
      conferenceId: responseId,
      state: state,
      removed: false,
    }));
  }

  private updateGuestState(guestId: string, newState: GuestState): Option<IGuest> {
    return this.findGuest(guestId).map(guest => {
      let oldState = guest.state;
      guest.state = newState;
      this.saveConferences();
      if (guest.state !== oldState) {
        this._guestState$.next(guest);
      }
      this.$log.info(`[conference] updated guest with id[${guestId}] state[${GuestState[oldState]} -> ${GuestState[newState]}]`);
      return guest;
    });
  }

  private getContact(responseId: string): IPromise<IGuest> {
    let deferred = this.$q.defer<IGuest>();
    this.contactDeprecatedService.getContact(responseId).then(
      contact => {
        let guestOpt = this.getGuest(responseId, contact.connectionId);
        if (guestOpt.isDefined()) {
          deferred.resolve(guestOpt.get());
        } else {
          deferred.reject();
        }
      },
      () => {
        deferred.reject();
      }
    );
    return deferred.promise;
  }
}

export default MODULE_NAME;
